import { useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { SKY_DOME_RADIUS_M } from "./viewerCameraLimits";
import type { TimeOfDayPresentationTarget } from "./viewerConfig";
import { SUN_KEY_AZIMUTH_DEG, withSunAzimuth } from "./viewerConfig";

interface SceneSkyProps {
	/**
	 * The graded presentation target for the current time of day. Owned by
	 * `SceneEnvironment` and passed in rather than re-derived here: the
	 * dressing hedge washes toward `backgroundHorizon` too, and two
	 * independent calls to `getTimeOfDayPresentationTarget` are two things
	 * to keep in step the moment its signature grows an argument.
	 */
	target: TimeOfDayPresentationTarget;
}

/**
 * The moon rides the same azimuth as the night key light (issue 121) —
 * it used to hang on the opposite side of the sky from the direction
 * the moonlight was actually coming from. Authored as a 19.7 m radius
 * at 12 m up, then re-aimed by the shared constant so re-aiming the sun
 * can never leave the moon behind.
 */
const MOON_POSITION = withSunAzimuth([-8, 12, -18], SUN_KEY_AZIMUTH_DEG);

/**
 * Distance fog band. Camera-relative: the sauna sits ~5–9 m from the
 * default presets and stays clean; the far side of the hedge arc
 * (~20 m from the camera) picks up a little horizon haze,
 * and the ground plane's far edge (~25–30 m) fades toward the sky
 * horizon colour instead of ending in a hard silhouette line.
 */
const FOG_NEAR_M = 16;
const FOG_FAR_M = 48;

const SKY_VERTEX_SHADER = /* glsl */ `
varying vec3 vWorldDir;
void main() {
	vec4 worldPosition = modelMatrix * vec4(position, 1.0);
	// Dome is origin-centred, so the vertex position IS the view ray.
	vWorldDir = normalize(worldPosition.xyz);
	gl_Position = projectionMatrix * viewMatrix * worldPosition;
}
`;

/**
 * Three-stop gradient (ground → horizon → zenith) plus a two-lobe sun
 * halo. Authored display colours: the shader deliberately has no
 * tone-mapping chunk, so the AgX exposure compensation that blew the
 * old Preetham horizon out to white cannot clip these values. A tiny
 * screen-space dither breaks gradient banding (same trick as
 * LightShafts).
 */
const SKY_FRAGMENT_SHADER = /* glsl */ `
uniform vec3 uZenithColor;
uniform vec3 uHorizonColor;
uniform vec3 uGroundColor;
uniform float uHorizonSoftness;
uniform vec3 uSunDirection;
uniform vec3 uSunGlowColor;
uniform float uSunGlowIntensity;
varying vec3 vWorldDir;
float hash(vec2 p) {
	return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}
void main() {
	vec3 dir = normalize(vWorldDir);
	// Above the horizon: blend horizon → zenith across the softness
	// band; the pow keeps a visible haze body low in the sky.
	float up = smoothstep(0.0, uHorizonSoftness, max(dir.y, 0.0));
	vec3 color = mix(uHorizonColor, uZenithColor, pow(up, 1.35));
	// Below the horizon: fall off quickly into the ground tone.
	float down = smoothstep(0.0, 0.14, max(-dir.y, 0.0));
	color = mix(color, uGroundColor, down);
	// Sun: wide atmospheric halo + tighter core disc glow.
	float towardSun = max(dot(dir, normalize(uSunDirection)), 0.0);
	float halo = pow(towardSun, 14.0);
	float core = pow(towardSun, 240.0);
	color += uSunGlowColor * (halo * 0.35 + core * 0.9) * uSunGlowIntensity;
	color += (hash(gl_FragCoord.xy) - 0.5) * 0.008;
	gl_FragColor = vec4(color, 1.0);
}
`;

export function SceneSky({ target }: SceneSkyProps) {
	const invalidate = useThree((state) => state.invalidate);
	const sceneReadonly = useThree((state) => state.scene);
	// Same ref indirection as SceneEnvironment: the react-hooks
	// immutability rule (correctly) flags direct mutation of a
	// use-store value, but assigning scene.fog is exactly how three.js
	// is driven.
	const sceneRef = useRef<THREE.Scene | null>(null);

	useEffect(() => {
		sceneRef.current = sceneReadonly;
	}, [sceneReadonly]);

	// ONE stable uniforms object for the dome's lifetime. three.js caches
	// the uniform references a ShaderMaterial was compiled with, so
	// swapping in a fresh uniforms object per time-of-day change would
	// silently keep uploading the original values. The per-target values
	// are applied declaratively through R3F's `uniforms-*-value` props
	// below — R3F writes into these entries and invalidates the demand
	// frameloop itself (no per-frame loop, no manual mutation).
	const uniforms = useMemo(
		() => ({
			uZenithColor: { value: new THREE.Color() },
			uHorizonColor: { value: new THREE.Color() },
			uGroundColor: { value: new THREE.Color() },
			uHorizonSoftness: { value: 0.4 },
			uSunDirection: { value: new THREE.Vector3(0, 1, 0) },
			uSunGlowColor: { value: new THREE.Color() },
			uSunGlowIntensity: { value: 0 },
		}),
		[],
	);

	const skyUniformValues = useMemo(
		() => ({
			zenith: new THREE.Color(target.backgroundTop),
			horizon: new THREE.Color(target.backgroundHorizon),
			ground: new THREE.Color(target.backgroundBottom),
			sunDirection: new THREE.Vector3(...target.sunPosition),
			sunGlow: new THREE.Color(target.sunGlowColor),
		}),
		[target],
	);

	// Time-of-day-tinted distance fog: seats the ground plane's far edge
	// (and the far side of the hedge) into the dome's horizon band. The
	// dome shader itself has no fog chunk, so only real scene geometry
	// participates. scene.fog is not an R3F-managed prop, so repaint the
	// demand frameloop explicitly.
	useEffect(() => {
		const scene = sceneRef.current;
		if (!scene) return;
		const fog = new THREE.Fog(
			new THREE.Color(target.backgroundHorizon),
			FOG_NEAR_M,
			FOG_FAR_M,
		);
		scene.fog = fog;
		invalidate();
		return () => {
			if (scene.fog === fog) {
				scene.fog = null;
			}
		};
	}, [target.backgroundHorizon, invalidate]);

	const starPositions = useMemo(() => {
		const count = 180;
		const positions = new Float32Array(count * 3);
		for (let index = 0; index < count; index += 1) {
			const seed = index + 1;
			const theta = (seed * 2.399963229728653) % (Math.PI * 2);
			const y = 0.18 + ((seed * 37) % 82) / 100;
			const radius = Math.sqrt(Math.max(0, 1 - y * y)) * 42;
			positions[index * 3] = Math.cos(theta) * radius;
			positions[index * 3 + 1] = y * 42;
			positions[index * 3 + 2] = Math.sin(theta) * radius;
		}
		return positions;
	}, []);

	return (
		<>
			{/*
			 * The dome radius (matching the night-overlay radius it
			 * replaces) lives in `viewerCameraLimits` because it is one
			 * half of the viewer's projection invariant:
			 * CAMERA_FAR_M > MAX_ORBIT_DISTANCE_M + SKY_DOME_RADIUS_M
			 * (110 > 26 + 70). If the far plane ever stops covering the
			 * dome's far wall, the clipped half shows the page
			 * background through the transparent canvas as a growing
			 * dark ring. The stars (42 m radius) and the moon (~22.6 m
			 * from origin) sit well inside the dome, so the same margin
			 * covers them.
			 */}
			<mesh name="sky-dome" renderOrder={-20} raycast={() => null}>
				<sphereGeometry args={[SKY_DOME_RADIUS_M, 32, 24]} />
				<shaderMaterial
					vertexShader={SKY_VERTEX_SHADER}
					fragmentShader={SKY_FRAGMENT_SHADER}
					uniforms={uniforms}
					uniforms-uZenithColor-value={skyUniformValues.zenith}
					uniforms-uHorizonColor-value={skyUniformValues.horizon}
					uniforms-uGroundColor-value={skyUniformValues.ground}
					uniforms-uHorizonSoftness-value={target.horizonSoftness}
					uniforms-uSunDirection-value={skyUniformValues.sunDirection}
					uniforms-uSunGlowColor-value={skyUniformValues.sunGlow}
					uniforms-uSunGlowIntensity-value={target.sunGlowIntensity}
					side={THREE.BackSide}
					depthWrite={false}
					fog={false}
				/>
			</mesh>
			<points renderOrder={-10}>
				<bufferGeometry>
					<bufferAttribute
						attach="attributes-position"
						args={[starPositions, 3]}
					/>
				</bufferGeometry>
				<pointsMaterial
					size={0.075}
					sizeAttenuation
					color="#f6efe2"
					transparent
					opacity={target.starOpacity * 0.82}
					depthWrite={false}
					fog={false}
				/>
			</points>
			<mesh position={MOON_POSITION} renderOrder={-8}>
				<sphereGeometry args={[0.42, 24, 12]} />
				<meshBasicMaterial
					color="#f1ead7"
					transparent
					opacity={target.moonOpacity}
					depthWrite={false}
					fog={false}
				/>
			</mesh>
		</>
	);
}
